import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { MathBlock } from "../components/MathRender";
import { ArrowLeft } from "lucide-react";

export default function NewProblem() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    title: "", statement_latex: "f(x) = ", domain: "Analysis", difficulty: "Intermediate",
    acceptance_criteria: "", deadline: "", prize_amount: 500, review_budget: 150,
    ip_terms: "Work-for-hire: full rights transfer to customer on accepted payout.",
    allow_ai: true, award_count: 1, tie_rule: "Earliest valid submission wins on ties.",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...f,
        prize_amount: Number(f.prize_amount),
        review_budget: Number(f.review_budget),
        award_count: Number(f.award_count),
        deadline: f.deadline ? new Date(f.deadline).toISOString() : null,
      };
      const { data } = await api.post("/solve/problems", payload);
      toast.success("Draft created — fund the escrow to open it");
      navigate(`/solve/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create");
    } finally { setLoading(false); }
  };

  const input = "mt-2 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]";

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="new-problem-page">
      <button onClick={() => navigate("/solve")} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Cancel
      </button>
      <p className="eyebrow mb-2">Commission</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">Draft a new problem</h1>
      <p className="mt-2 text-slate-500">Fix acceptance criteria, deadline, award, and IP terms before funding.</p>

      <form onSubmit={submit} className="axiom-card mt-8 space-y-5 p-7">
        <div><label className="eyebrow">Title</label><input data-testid="np-title" required value={f.title} onChange={set("title")} className={input} /></div>
        <div>
          <label className="eyebrow">Problem statement (LaTeX)</label>
          <textarea data-testid="np-statement" required rows={3} value={f.statement_latex} onChange={set("statement_latex")}
            className={`${input} font-mono text-[#B45309]`} />
          <div className="mt-2 rounded-lg border border-[#CBD5E1] bg-slate-50 p-3"><MathBlock>{f.statement_latex}</MathBlock></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="eyebrow">Domain</label><input data-testid="np-domain" value={f.domain} onChange={set("domain")} className={input} /></div>
          <div>
            <label className="eyebrow">Difficulty</label>
            <select data-testid="np-difficulty" value={f.difficulty} onChange={set("difficulty")} className={input}>
              {["Foundational", "Intermediate", "Advanced", "Expert"].map((d) => <option key={d} className="bg-[#FFFFFF]">{d}</option>)}
            </select>
          </div>
        </div>
        <div><label className="eyebrow">Acceptance criteria</label><textarea data-testid="np-criteria" required rows={3} value={f.acceptance_criteria} onChange={set("acceptance_criteria")} className={input} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="eyebrow">Award amount (USD)</label><input data-testid="np-prize" type="number" min={0} value={f.prize_amount} onChange={set("prize_amount")} className={input} /></div>
          <div><label className="eyebrow">Review budget (USD)</label><input data-testid="np-review-budget" type="number" min={0} value={f.review_budget} onChange={set("review_budget")} className={input} /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="eyebrow">Deadline</label><input data-testid="np-deadline" type="date" value={f.deadline} onChange={set("deadline")} className={input} /></div>
          <div><label className="eyebrow">Award count</label><input data-testid="np-award-count" type="number" min={1} value={f.award_count} onChange={set("award_count")} className={input} /></div>
        </div>
        <div><label className="eyebrow">IP &amp; rights terms</label><textarea data-testid="np-ip" rows={2} value={f.ip_terms} onChange={set("ip_terms")} className={input} /></div>
        <div><label className="eyebrow">Tie rule</label><input data-testid="np-tie" value={f.tie_rule} onChange={set("tie_rule")} className={input} /></div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input data-testid="np-allow-ai" type="checkbox" checked={f.allow_ai} onChange={set("allow_ai")} className="h-4 w-4 accent-[#E61A27]" />
          Permit disclosed AI assistance
        </label>
        <button data-testid="np-submit" disabled={loading} className="btn-crimson w-full">{loading ? "Creating…" : "Create draft"}</button>
      </form>
    </div>
  );
}
